import useStory, { span, ease } from './useStory';
import { Explainer } from './GuideParts';

// GUIDE 1'S ANIMATION (Oct 2026): two rooms, each with a non-inverter and an
// inverter AC, all set to 24°C. In the bedroom the room reaches 24°C, the
// inverter slows down and the non-inverter switches on and off. In the
// top-floor hall the room never gets there, so both stay at full power. The
// rupees under each AC and the hall's warmest temperature come from the room
// model (twoRooms() in data/guideInverter.js); the curves between are drawn
// simply. It plays once by itself in view, rests fully drawn, and has Replay.
//
// Props: rooms (twoRooms()), watch ({ title, captions, note, noteSrc }), sources.

const START = { bed: 31, hall: 34 };
const REACH = { bed: 0.3, hall: 0.55 };   // when each room stops falling

const inr = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;

function Bar({ share, tone }) {
  return (
    <div className="h-3 overflow-hidden rounded-full bg-gray-100" aria-hidden="true">
      <div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.round(share * 100)}%`, transition: 'width 120ms linear' }} />
    </div>
  );
}

function AcRow({ name, power, status, rupees, tone, hours }) {
  return (
    <div className="grid content-start gap-1.5 rounded-xl bg-gray-50 p-2.5">
      <span className="text-[14px] font-bold leading-tight text-gray-900">{name}</span>
      <span className="w-fit rounded-full bg-white px-2 py-0.5 text-[12px] font-bold text-gray-700 ring-1 ring-gray-200">{status}</span>
      <Bar share={power} tone={tone} />
      <p className="text-[12px] text-gray-600"><b className="text-[20px] font-extrabold tabular-nums text-gray-900">{inr(rupees)}</b> for {hours} hrs</p>
    </div>
  );
}

export default function TwoRooms({ rooms, watch, sources }) {
  const story = useStory(22000);
  const t = story.t;

  return (
    <Explainer title={watch.title} captions={watch.captions} story={story} note={watch.note} noteSrc={watch.noteSrc} sources={sources}>
      <div className="grid gap-3 sm:grid-cols-2">
        {rooms.map((r) => {
          const reach = REACH[r.key];
          const goal = r.key === 'bed' ? 24 : Math.max(r.fixed.warmest, r.inv.warmest);
          const k = ease(span(t, 0, reach));
          const temp = START[r.key] + (goal - START[r.key]) * k;
          const cool = r.fixed.flat === 0 && r.inv.flat === 0;
          const after = span(t, reach, reach + 0.08);
          // Non-inverter: full power, then on and off for its share of time.
          const cycle = ((t - reach) * 40) % 1;
          const fOn = !cool || t < reach || t >= 1 || cycle < Math.max(0.35, r.fixed.share);
          const fPower = fOn ? 1 : 0;
          // Inverter: full power, then slows down to its share.
          const iPower = cool ? 1 - (1 - Math.max(0.25, r.inv.share)) * after : 1;
          const done = t >= 0.999;
          return (
            <div key={r.key} className="grid content-start gap-2.5 rounded-xl border border-gray-200 p-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-[16px] font-extrabold leading-tight text-gray-900">{r.label}</p>
                  <p className="text-[13px] leading-snug text-gray-500">{r.note}</p>
                </div>
                <div className="text-right">
                  <p className={`text-[28px] font-extrabold leading-none tabular-nums ${temp <= 24.05 ? 'text-green-700' : 'text-gray-900'}`}>{temp.toFixed(1)}°</p>
                  <p className="mt-0.5 text-[12px] text-gray-500">Set to 24°</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
              <AcRow name="Non-inverter" power={fPower} tone="bg-gray-500" hours={r.hours}
                status={!cool || t < reach ? 'Full power' : done ? 'On and off' : (fOn ? 'On' : 'Off')}
                rupees={r.fixed.rupees * t} />
              <AcRow name="Inverter" power={iPower} tone="bg-blue-600" hours={r.hours}
                status={!cool || t < reach ? 'Full power' : 'Slowed down'}
                rupees={r.inv.rupees * t} />
              </div>
            </div>
          );
        })}
      </div>
    </Explainer>
  );
}
